'use client'

import { forwardRef, useState, useCallback, useRef, useEffect } from 'react'
import { Upload, File, FileText, Image, X, Check, AlertCircle, RotateCcw } from 'lucide-react'
import { safeAccess, safeArrayAccess } from '../utils/security'

/**
 * BIRHAUS File Upload Component
 * 
 * Enhanced file upload implementing all 10 BIRHAUS principles:
 * - Spanish-first status messages and validation
 * - Progressive disclosure of upload progress and validation
 * - Cognitive load reduction (max 3 files at once)
 * - Accessibility compliance (WCAG AA+)
 * - Undo functionality for file removals
 * - Auto-validation for file types and sizes
 * - Performance optimized with progress tracking
 * - IPU Admin specific file types (receipts, reports, photos)
 */

// BIRHAUS: Utility function (self-contained)
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

export interface FileItem {
  id: string
  file: File
  preview?: string
  progress: number
  status: 'pending' | 'uploading' | 'completed' | 'error'
  error?: string
}

interface UndoState {
  removedFile: FileItem
  timeout: NodeJS.Timeout | null
  isActive: boolean
}

interface FileTypeConfig {
  extensions: string[]
  maxSize: number // in bytes
  labelEs: string
  labelEn: string
  icon: React.ReactNode
}

export interface BirhausFileUploadProps {
  // BIRHAUS Principle #7: Bilingual by design
  label?: string
  labelEs?: string
  labelEn?: string
  
  // BIRHAUS Principle #4: Progressive disclosure
  description?: string
  descriptionEs?: string
  descriptionEn?: string
  
  // File handling
  files?: FileItem[]
  defaultFiles?: FileItem[]
  onChange?: (files: FileItem[]) => void
  onUpload?: (file: File) => Promise<string> // Returns URL or ID
  onRemove?: (fileId: string) => Promise<void>
  
  // BIRHAUS Principle #2: Honest data
  error?: string
  errorEs?: string
  errorEn?: string
  
  // File validation
  acceptedTypes?: string[] // e.g., ['image/*', '.pdf', '.xlsx']
  maxFileSize?: number // in bytes (default 10MB)
  maxFiles?: number // default 3 (cognitive load reduction)
  
  // File type presets for IPU Admin
  uploadType?: 'receipts' | 'reports' | 'photos' | 'documents' | 'custom'
  
  // BIRHAUS Principle #5: Undo over confirm
  enableUndo?: boolean
  undoTimeoutMs?: number
  
  // BIRHAUS Principle #6: Accessibility = dignity
  disabled?: boolean
  required?: boolean
  
  // Appearance
  variant?: 'default' | 'compact' | 'minimal'
  size?: 'sm' | 'md' | 'lg'
  
  // Drag and drop
  enableDragDrop?: boolean
  
  // Locale
  locale?: 'es' | 'en'
  
  // Preview options
  showPreview?: boolean
  previewSize?: 'sm' | 'md' | 'lg'
}

export const BirhausFileUpload = forwardRef<HTMLDivElement, BirhausFileUploadProps>(({
  // Labels (Spanish-first)
  label,
  labelEs,
  labelEn,
  
  // Description
  description,
  descriptionEs,
  descriptionEn,
  
  // File handling
  files,
  defaultFiles = [],
  onChange,
  onUpload,
  onRemove,
  
  // Validation
  error,
  errorEs,
  errorEn,
  acceptedTypes,
  maxFileSize = 10 * 1024 * 1024, // 10MB
  maxFiles = 3,
  
  // Upload type
  uploadType = 'documents',
  
  // Undo
  enableUndo = true,
  undoTimeoutMs = 10000,
  
  // Basic props
  disabled = false,
  required = false,
  
  // Appearance
  variant = 'default',
  size = 'md',
  
  // Features
  enableDragDrop = true,
  
  // Locale
  locale = 'es',
  
  // Preview
  showPreview = true,
  previewSize = 'md'
}, ref) => {
  // State management
  const [internalFiles, setInternalFiles] = useState<FileItem[]>(files || defaultFiles)
  const [isDragOver, setIsDragOver] = useState(false)
  const [undoState, setUndoState] = useState<UndoState | null>(null)
  const [validationError, setValidationError] = useState<string>('')
  
  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dropZoneRef = useRef<HTMLDivElement>(null)
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const getDisplayText = useCallback((
    primary: string | undefined,
    spanish: string | undefined,
    english: string | undefined,
    fallback: string
  ): string => {
    if (locale === 'es') {
      return primary || spanish || english || fallback
    }
    return primary || english || spanish || fallback
  }, [locale])
  
  const displayLabel = getDisplayText(label, labelEs, labelEn, '')
  const displayDescription = getDisplayText(description, descriptionEs, descriptionEn, '')
  const displayError = error || errorEs || validationError
  
  // File type configurations for IPU Admin
  const getFileTypeConfig = useCallback((): FileTypeConfig => {
    const configs: Record<string, FileTypeConfig> = {
      receipts: {
        extensions: ['.pdf', '.jpg', '.jpeg', '.png'],
        maxSize: 5 * 1024 * 1024, // 5MB
        labelEs: 'Recibos',
        labelEn: 'Receipts',
        icon: <FileText className="h-5 w-5" />
      },
      reports: {
        extensions: ['.pdf', '.xlsx', '.xls', '.csv'],
        maxSize: 25 * 1024 * 1024, // 25MB
        labelEs: 'Reportes',
        labelEn: 'Reports',
        icon: <File className="h-5 w-5" />
      },
      photos: {
        extensions: ['.jpg', '.jpeg', '.png', '.webp'],
        maxSize: 10 * 1024 * 1024, // 10MB
        labelEs: 'Fotos',
        labelEn: 'Photos',
        icon: <Image className="h-5 w-5" />
      },
      documents: {
        extensions: ['.pdf', '.doc', '.docx', '.txt'],
        maxSize: 15 * 1024 * 1024, // 15MB
        labelEs: 'Documentos',
        labelEn: 'Documents',
        icon: <FileText className="h-5 w-5" />
      },
      custom: {
        extensions: acceptedTypes || ['*'],
        maxSize: maxFileSize,
        labelEs: 'Archivos',
        labelEn: 'Files',
        icon: <File className="h-5 w-5" />
      }
    }
    
    return safeAccess(configs, uploadType, configs.custom)
  }, [uploadType, acceptedTypes, maxFileSize])
  
  const fileTypeConfig = getFileTypeConfig()
  
  // Generate unique ID for files
  const generateFileId = useCallback(() => {
    return Date.now().toString(36) + Math.random().toString(36).substr(2)
  }, [])
  
  // Validate file
  const validateFile = useCallback((file: File): string => {
    // Check file size
    if (file.size > fileTypeConfig.maxSize) {
      const sizeMB = Math.round(fileTypeConfig.maxSize / 1024 / 1024)
      return locale === 'es' 
        ? `El archivo es muy grande. Máximo ${sizeMB}MB permitido.`
        : `File is too large. Maximum ${sizeMB}MB allowed.`
    }
    
    // Check file type
    if (fileTypeConfig.extensions[0] !== '*') {
      const extension = '.' + file.name.split('.').pop()?.toLowerCase()
      const isValidType = fileTypeConfig.extensions.some(ext => 
        ext === extension || file.type.match(ext.replace('*', '.*'))
      )
      
      if (!isValidType) {
        return locale === 'es'
          ? `Tipo de archivo no permitido. Use: ${fileTypeConfig.extensions.join(', ')}`
          : `File type not allowed. Use: ${fileTypeConfig.extensions.join(', ')}`
      }
    }
    
    // Check max files
    if (internalFiles.length >= maxFiles) {
      return locale === 'es'
        ? `Máximo ${maxFiles} archivos permitidos`
        : `Maximum ${maxFiles} files allowed`
    }
    
    return ''
  }, [fileTypeConfig, internalFiles.length, maxFiles, locale])
  
  // Create file preview
  const createFilePreview = useCallback((file: File): Promise<string | undefined> => {
    return new Promise((resolve) => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader()
        reader.onload = (e) => resolve(e.target?.result as string)
        reader.onerror = () => resolve(undefined)
        reader.readAsDataURL(file)
      } else {
        resolve(undefined)
      }
    })
  }, [])
  
  // Handle undo functionality
  const handleUndo = useCallback(() => {
    if (undoState?.isActive && undoState.timeout) {
      clearTimeout(undoState.timeout)
      const restoredFiles = [...internalFiles, undoState.removedFile]
      setInternalFiles(restoredFiles)
      onChange?.(restoredFiles)
      setUndoState(null)
    }
  }, [undoState, internalFiles, onChange])
  
  // Setup undo for file removal
  const setupUndo = useCallback((removedFile: FileItem) => {
    if (!enableUndo) return
    
    // Clear existing timeout
    if (undoState?.timeout) {
      clearTimeout(undoState.timeout)
    }
    
    // Set up new undo state
    const timeout = setTimeout(() => {
      setUndoState(null)
    }, undoTimeoutMs)
    
    setUndoState({
      removedFile,
      timeout,
      isActive: true
    })
  }, [enableUndo, undoTimeoutMs, undoState?.timeout])
  
  // Add files
  const addFiles = useCallback(async (fileList: FileList | File[]) => {
    const filesArray = Array.from(fileList)
    const newFiles: FileItem[] = []
    
    for (const file of filesArray) {
      const validationResult = validateFile(file)
      if (validationResult) {
        setValidationError(validationResult)
        return
      }
      
      const preview = await createFilePreview(file)
      const fileItem: FileItem = {
        id: generateFileId(),
        file,
        preview,
        progress: 0,
        status: 'pending'
      }
      
      newFiles.push(fileItem)
    }
    
    if (newFiles.length > 0) {
      const updatedFiles = [...internalFiles, ...newFiles]
      setInternalFiles(updatedFiles)
      onChange?.(updatedFiles)
      setValidationError('')
      
      // Start upload if handler provided
      if (onUpload) {
        newFiles.forEach(fileItem => uploadFile(fileItem))
      }
    }
  }, [internalFiles, onChange, validateFile, createFilePreview, generateFileId, onUpload])
  
  // Upload file
  const uploadFile = useCallback(async (fileItem: FileItem) => {
    if (!onUpload) return
    
    // Update status to uploading
    setInternalFiles(prev => prev.map(f => 
      f.id === fileItem.id 
        ? { ...f, status: 'uploading' as const, progress: 0 }
        : f
    ))
    
    try {
      // Simulate progress (replace with actual upload progress)
      let progress = 0
      const progressInterval = setInterval(() => {
        progress += Math.random() * 30
        if (progress >= 90) {
          clearInterval(progressInterval)
          progress = 90
        }
        
        setInternalFiles(prev => prev.map(f => 
          f.id === fileItem.id ? { ...f, progress } : f
        ))
      }, 100)
      
      // Perform actual upload
      const result = await onUpload(fileItem.file)
      
      clearInterval(progressInterval)
      
      // Update to completed
      setInternalFiles(prev => prev.map(f => 
        f.id === fileItem.id 
          ? { ...f, status: 'completed' as const, progress: 100 }
          : f
      ))
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed'
      
      setInternalFiles(prev => prev.map(f => 
        f.id === fileItem.id 
          ? { 
              ...f, 
              status: 'error' as const, 
              progress: 0,
              error: errorMessage
            }
          : f
      ))
    }
  }, [onUpload])
  
  // Remove file
  const removeFile = useCallback(async (fileId: string) => {
    const fileToRemove = internalFiles.find(f => f.id === fileId)
    if (!fileToRemove) return
    
    const updatedFiles = internalFiles.filter(f => f.id !== fileId)
    setInternalFiles(updatedFiles)
    onChange?.(updatedFiles)
    
    // Setup undo
    setupUndo(fileToRemove)
    
    // Call remove handler
    if (onRemove) {
      try {
        await onRemove(fileId)
      } catch (error) {
        console.error('Failed to remove file:', error)
      }
    }
  }, [internalFiles, onChange, setupUndo, onRemove])
  
  // Handle file input change
  const handleFileInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files) {
      addFiles(files)
    }
    // Reset input value to allow selecting same file again
    event.target.value = ''
  }, [addFiles])
  
  // Handle drag and drop
  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    if (!disabled && enableDragDrop) {
      setIsDragOver(true)
    }
  }, [disabled, enableDragDrop])
  
  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    if (!dropZoneRef.current?.contains(event.relatedTarget as Node)) {
      setIsDragOver(false)
    }
  }, [])
  
  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    setIsDragOver(false)
    
    if (!disabled && enableDragDrop) {
      const files = event.dataTransfer.files
      if (files) {
        addFiles(files)
      }
    }
  }, [disabled, enableDragDrop, addFiles])
  
  // Get file icon
  const getFileIcon = useCallback((file: File) => {
    if (file.type.startsWith('image/')) {
      return <Image className="h-4 w-4" />
    } else if (file.type === 'application/pdf') {
      return <FileText className="h-4 w-4" />
    } else {
      return <File className="h-4 w-4" />
    }
  }, [])
  
  // Format file size
  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + safeArrayAccess(sizes, i, 'Bytes')
  }, [])
  
  // Sync external files
  useEffect(() => {
    if (files && files !== internalFiles) {
      setInternalFiles(files)
    }
  }, [files, internalFiles])
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (undoState?.timeout) {
        clearTimeout(undoState.timeout)
      }
    }
  }, [undoState?.timeout])
  
  // Style classes
  const sizeClasses = {
    sm: 'p-3',
    md: 'p-4',
    lg: 'p-6'
  }
  
  const variantClasses = {
    default: 'border-2 border-dashed border-gray-300',
    compact: 'border border-gray-300 rounded-md',
    minimal: 'border-b border-gray-300'
  }
  
  return (
    <div ref={ref} className="birhaus-file-upload">
      {/* Label */}
      {displayLabel && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {displayLabel}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      {/* Description */}
      {displayDescription && (
        <p className="text-sm text-gray-600 mb-2">
          {displayDescription}
        </p>
      )}
      
      {/* File Type Info */}
      <div className="mb-3 text-sm text-gray-600">
        <div className="flex items-center space-x-2">
          {fileTypeConfig.icon}
          <span>
            {locale === 'es' ? fileTypeConfig.labelEs : fileTypeConfig.labelEn}
          </span>
          <span className="text-gray-400">•</span>
          <span>
            {locale === 'es' ? 'Máximo' : 'Max'} {formatFileSize(fileTypeConfig.maxSize)}
          </span>
          <span className="text-gray-400">•</span>
          <span>
            {locale === 'es' ? 'Hasta' : 'Up to'} {maxFiles} {locale === 'es' ? 'archivos' : 'files'}
          </span>
        </div>
        {fileTypeConfig.extensions[0] !== '*' && (
          <div className="mt-1 text-xs text-gray-500">
            {locale === 'es' ? 'Tipos permitidos' : 'Allowed types'}: {fileTypeConfig.extensions.join(', ')}
          </div>
        )}
      </div>
      
      {/* Drop Zone */}
      <div
        ref={dropZoneRef}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'relative transition-all duration-200 rounded-lg',
          safeAccess(sizeClasses, size, sizeClasses.md),
          safeAccess(variantClasses, variant, variantClasses.default),
          isDragOver && 'border-primary-500 bg-primary-50',
          disabled && 'opacity-50 cursor-not-allowed',
          displayError && 'border-red-300'
        )}
      >
        {/* Upload Area */}
        <div className="text-center">
          <Upload className={cn(
            'mx-auto mb-2',
            size === 'sm' ? 'h-6 w-6' : size === 'lg' ? 'h-10 w-10' : 'h-8 w-8',
            isDragOver ? 'text-primary-500' : 'text-gray-400'
          )} />
          
          <div className="space-y-1">
            <div className={cn(
              'font-medium',
              size === 'sm' ? 'text-sm' : 'text-base',
              isDragOver ? 'text-primary-700' : 'text-gray-700'
            )}>
              {enableDragDrop ? (
                locale === 'es' 
                  ? 'Arrastra archivos aquí o' 
                  : 'Drag files here or'
              ) : (
                locale === 'es'
                  ? 'Selecciona archivos'
                  : 'Select files'
              )}
              {enableDragDrop && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={disabled}
                  className="ml-1 text-primary-600 hover:text-primary-700 underline"
                >
                  {locale === 'es' ? 'busca en tu dispositivo' : 'browse'}
                </button>
              )}
            </div>
            
            <div className="text-sm text-gray-500">
              {internalFiles.length > 0 && (
                <span>
                  {internalFiles.length} de {maxFiles} {locale === 'es' ? 'archivos' : 'files'}
                </span>
              )}
            </div>
          </div>
        </div>
        
        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple={maxFiles > 1}
          accept={fileTypeConfig.extensions.join(',')}
          onChange={handleFileInputChange}
          disabled={disabled}
          className="hidden"
        />
        
        {/* Manual Upload Button for non-drag drop */}
        {!enableDragDrop && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            className={cn(
              'mt-2 w-full px-4 py-2 border border-gray-300 rounded-md text-sm font-medium',
              'text-gray-700 bg-white hover:bg-gray-50',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50',
              'disabled:cursor-not-allowed disabled:bg-gray-50'
            )}
          >
            {locale === 'es' ? 'Seleccionar archivos' : 'Select files'}
          </button>
        )}
      </div>
      
      {/* File List */}
      {internalFiles.length > 0 && (
        <div className="mt-4 space-y-2">
          {internalFiles.map((fileItem) => (
            <div
              key={fileItem.id}
              className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg"
            >
              {/* File Icon/Preview */}
              <div className="flex-shrink-0">
                {showPreview && fileItem.preview ? (
                  <img
                    src={fileItem.preview}
                    alt={fileItem.file.name}
                    className={cn(
                      'rounded object-cover',
                      previewSize === 'sm' ? 'h-8 w-8' :
                      previewSize === 'lg' ? 'h-16 w-16' : 'h-12 w-12'
                    )}
                  />
                ) : (
                  <div className="text-gray-400">
                    {getFileIcon(fileItem.file)}
                  </div>
                )}
              </div>
              
              {/* File Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {fileItem.file.name}
                  </p>
                  <div className="flex items-center space-x-2">
                    {/* Status Icon */}
                    {fileItem.status === 'completed' && (
                      <Check className="h-4 w-4 text-green-500" />
                    )}
                    {fileItem.status === 'error' && (
                      <AlertCircle className="h-4 w-4 text-red-500" />
                    )}
                    {fileItem.status === 'uploading' && (
                      <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-primary-600 rounded-full" />
                    )}
                    
                    {/* Remove Button */}
                    <button
                      type="button"
                      onClick={() => removeFile(fileItem.id)}
                      disabled={disabled}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                
                <div className="flex items-center justify-between mt-1">
                  <p className="text-sm text-gray-500">
                    {formatFileSize(fileItem.file.size)}
                  </p>
                  
                  {/* Progress or Status */}
                  {fileItem.status === 'uploading' && (
                    <div className="flex items-center space-x-2">
                      <div className="w-20 bg-gray-200 rounded-full h-1.5">
                        <div
                          className="bg-primary-600 h-1.5 rounded-full transition-all duration-300"
                          style={{ width: `${fileItem.progress}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500">
                        {Math.round(fileItem.progress)}%
                      </span>
                    </div>
                  )}
                  
                  {fileItem.status === 'completed' && (
                    <span className="text-xs text-green-600">
                      {locale === 'es' ? 'Completado' : 'Completed'}
                    </span>
                  )}
                  
                  {fileItem.status === 'error' && (
                    <span className="text-xs text-red-600">
                      {fileItem.error || (locale === 'es' ? 'Error' : 'Error')}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Error Display */}
      {displayError && (
        <div className="mt-2 text-sm text-red-600 flex items-start space-x-1">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{displayError}</span>
        </div>
      )}
      
      {/* Undo Notification */}
      {undoState?.isActive && (
        <div className="mt-2 bg-gray-800 text-white text-sm rounded-md p-3 flex items-center justify-between">
          <span>
            {locale === 'es' 
              ? `Archivo "${undoState.removedFile.file.name}" eliminado`
              : `File "${undoState.removedFile.file.name}" removed`
            }
          </span>
          <button
            type="button"
            onClick={handleUndo}
            className="flex items-center space-x-1 text-primary-200 hover:text-primary-100 font-medium"
          >
            <RotateCcw className="h-3 w-3" />
            <span>{locale === 'es' ? 'Deshacer' : 'Undo'}</span>
          </button>
        </div>
      )}
    </div>
  )
})

BirhausFileUpload.displayName = 'BirhausFileUpload'

// Hook for enhanced file upload functionality
export const useBirhausFileUpload = (maxFiles: number = 3) => {
  const [files, setFiles] = useState<FileItem[]>([])
  const [isUploading, setIsUploading] = useState(false)
  
  const addFiles = useCallback((newFiles: FileItem[]) => {
    setFiles(prev => [...prev, ...newFiles].slice(0, maxFiles))
  }, [maxFiles])
  
  const removeFile = useCallback((fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId))
  }, [])
  
  const updateFileStatus = useCallback((fileId: string, updates: Partial<FileItem>) => {
    setFiles(prev => prev.map(f => 
      f.id === fileId ? { ...f, ...updates } : f
    ))
  }, [])
  
  const uploadAll = useCallback(async (uploadFn: (file: File) => Promise<string>) => {
    setIsUploading(true)
    
    const pendingFiles = files.filter(f => f.status === 'pending')
    
    for (const fileItem of pendingFiles) {
      try {
        updateFileStatus(fileItem.id, { status: 'uploading', progress: 0 })
        await uploadFn(fileItem.file)
        updateFileStatus(fileItem.id, { status: 'completed', progress: 100 })
      } catch (error) {
        updateFileStatus(fileItem.id, { 
          status: 'error', 
          error: error instanceof Error ? error.message : 'Upload failed' 
        })
      }
    }
    
    setIsUploading(false)
  }, [files, updateFileStatus])
  
  const hasErrors = files.some(f => f.status === 'error')
  const allCompleted = files.length > 0 && files.every(f => f.status === 'completed')
  const totalProgress = files.length > 0 
    ? files.reduce((acc, f) => acc + f.progress, 0) / files.length 
    : 0
  
  return {
    files,
    setFiles,
    addFiles,
    removeFile,
    updateFileStatus,
    uploadAll,
    isUploading,
    hasErrors,
    allCompleted,
    totalProgress
  }
}